/**
 * Messwerteingabe für die Beurteilung der Gradheit der Säule Scheinwerer-Einstellgerät
 */

import { observable, action } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import { bind } from '../../../utils/decorators';
import { FormControl, FormGroup, Table, Row, Col, InputGroup, HelpBlock } from 'react-bootstrap';
import VorgangFormularElement from '../VorgangFormularElement';


class Messpunkt {
  editable: boolean;
  @observable abweichung: number | undefined;
  @observable hoehe: number | undefined;
  constructor(editable: boolean, abweichung: number | undefined, hoehe: number | undefined) {
    this.editable = editable;
    this.abweichung = abweichung;
    this.hoehe = hoehe;
  }
}

export interface ISepBeurteilungSaeule {
  elementguid: string;
  kategorieguid: string;
  editable?: boolean;
  value?: string;
  element: VorgangFormularElement;
  onHandleChange?: (e: VorgangFormularElement) => void;
}

export interface ISepBeurteilungSaeuleState {
  messpunkte: Messpunkt[];
}

@observer
export default class SepBeurteilungSaeule extends React.Component<ISepBeurteilungSaeule, ISepBeurteilungSaeuleState> {

    @observable validationstate: 'success' | 'warning' | 'error' | undefined = 'error';

    @observable public deltaAbweichung: number | undefined = undefined;
    @observable public deltaHoehe: number | undefined = undefined;

    private maxDeltaAbweichung: number = 0.2;
    private minDeltaHoehe: number = 450;
    private maxDeltaHoehe: number = 550;

    daten: Array<Array<string>> = new Array<Array<string>>();

    constructor(props: ISepBeurteilungSaeule) {
      super(props);
      this.state = { messpunkte: new Array<Messpunkt>() };

      // Im Vorgang hinterlegte Werte parsen
      if (this.props.value) {
        try {
          this.daten = JSON.parse(this.props.value);
        } catch (e) {
          this.daten = [];
        }
      }

      for (let i = 0; i < 2; i++) {
        const hoehe: number | undefined = (this.daten.length > i && this.daten[i] !== null) ? parseInt(this.daten[i][0], 10) : undefined;
        const abweichung: number | undefined = (this.daten.length > i && this.daten[i] !== null) ? parseFloat(this.daten[i][1]) : undefined;
        const messpunkt = new Messpunkt(this.props.editable ? this.props.editable : true, abweichung, hoehe);
        this.state.messpunkte.push(messpunkt);
      }

      // Ergebnisdaten im Vorgang hinterlegt?
      if (this.daten.length > 2) {
        try {
          this.deltaHoehe = parseInt(this.daten[2][0], 10);
          this.deltaAbweichung = parseFloat(this.daten[2][1]);
        } catch (e) {
          this.deltaHoehe = undefined;
          this.deltaAbweichung = undefined;
        }
      }

      this.validate();
    }

    public statusBeurteilung() {
      if ((this.deltaAbweichung !== undefined && this.deltaAbweichung <= this.maxDeltaAbweichung) && (this.deltaHoehe !== undefined && (this.deltaHoehe >= this.minDeltaHoehe && this.deltaHoehe <= this.maxDeltaHoehe))) {
        return 'in Ordnung';
      } else {
        return 'nicht in Ordnung';
      }
    }

    @bind @action handleHoeheChange(messpunkt: Messpunkt, ev: React.FormEvent<FormControl>) {
      messpunkt.hoehe = (ev.target as HTMLInputElement).value !== '' ? (ev.target as HTMLInputElement).valueAsNumber : undefined;
      this.validate();
      if (this.props.onHandleChange) {
        this.props.onHandleChange(this.props.element);
      }
    }

    @bind @action handleAbweichungChanged(messpunkt: Messpunkt, ev: React.FormEvent<FormControl>) {
      messpunkt.abweichung = (ev.target as HTMLInputElement).value !== '' ? (ev.target as HTMLInputElement).valueAsNumber : undefined;
      this.validate();
      if (this.props.onHandleChange) {
        this.props.onHandleChange(this.props.element);
      }
    }

    private messpunktHoeheValidationState(p: Messpunkt, index: number) {
      let res: 'success' | 'warning' | 'error' | undefined = 'error';
      res = p.hoehe !== undefined ? undefined : 'error';
      // Im ersten Messpunkt muss die Höhe zwischen 500 und 800 mm liegen
      if (index === 0 && p.hoehe && (p.hoehe < 500 || p.hoehe > 800)) {
        res = 'warning';
      }
      // TODO: Im zweiten Messpunkt muss das Delta im Bereich 450~550 mm liegen
      if (index === 1 && this.deltaHoehe && (this.deltaHoehe < this.minDeltaHoehe || this.deltaHoehe > this.maxDeltaHoehe)) {
        res = 'warning';
      }
      return res;
    }

    // private messpunktMinHoehe(p: Messpunkt, index: number) {
    // }

    public validate() {
      this.validationstate = this.state.messpunkte.some(el => (el.hoehe === undefined || el.abweichung === undefined)) ? 'error' : 'success';

      // Beurteilung und Ergebnis berechnen

      const p0: Messpunkt = this.state.messpunkte[0];
      const p1: Messpunkt = this.state.messpunkte[1];

      if (this.state.messpunkte.some(el => (el.hoehe === undefined))) {
        this.deltaHoehe = undefined;
      } else {
        if (p0.hoehe !== undefined && p1.hoehe !== undefined) {
          this.deltaHoehe = Math.abs(p1.hoehe - p0.hoehe);
        }
      }

      if (this.state.messpunkte.some(el => (el.abweichung === undefined))) {
        this.deltaAbweichung = undefined;
      } else {
        if (p0.abweichung !== undefined && p1.abweichung !== undefined) {
          this.deltaAbweichung = Math.abs(p1.abweichung * 100.0 - p0.abweichung * 100.0);
          this.deltaAbweichung /= 100.0;
        }
      }

      this.props.element.validationstate = this.validationstate;
    }

    public renderMesswertTabelle() {
      let elemente = [];
      for (let i = 0; i < this.state.messpunkte.length; i++) {
        const p: Messpunkt = this.state.messpunkte[i];
        elemente.push(
          <tr key={`${this.props.kategorieguid}[${this.props.elementguid}][${i}][1]`}>
            <td style={{textAlign: 'center', verticalAlign: 'middle'}}>{i + 1}</td>
            <td>
              <FormGroup validationState={this.messpunktHoeheValidationState(p, i)} style={{margin: '0px'}}>
                <InputGroup>
                  <FormControl
                    readOnly={!this.props.editable}
                    step={1.0}
                    type="number"
                    min={i === 0 ? 500 : undefined}
                    max={i === 0 ? 800 : undefined}
                    name={`${this.props.kategorieguid}[${this.props.elementguid}][${i}][0]`}
                    defaultValue={(p.hoehe !== undefined) ? p.hoehe.toFixed(0) : ''}
                    onChange={(e) => this.handleHoeheChange(p, e)}/>
                  <InputGroup.Addon>mm</InputGroup.Addon>
                </InputGroup>
                {this.messpunktHoeheValidationState(p, i) === 'warning' && <HelpBlock style={{marginBottom: '0px', fontWeight: 100}} className="text-center">{i === 0 ? '500 bis 800 mm' : 'Delta 450 bis 550 mm'}</HelpBlock>}
              </FormGroup>
            </td>
            <td>
              <FormGroup validationState={p.abweichung !== undefined ? undefined : 'error'} style={{margin: '0px'}}>
                <InputGroup>
                  <FormControl
                    readOnly={!this.props.editable}
                    step={0.01}
                    type="number"
                    name={`${this.props.kategorieguid}[${this.props.elementguid}][${i}][1]`}
                    defaultValue={(p.abweichung !== undefined) ? p.abweichung.toFixed(2) : ''}
                    onChange={(e) => this.handleAbweichungChanged(p, e)}/>
                  <InputGroup.Addon>%</InputGroup.Addon>
                </InputGroup>
              </FormGroup>
            </td>
          </tr>
        );
      }
      return elemente;
    }

     public render() {
        return(
            <div>
              <Row>
                <Col xs={2}/>
                <Col xs={8}>
                  <Table striped={true} bordered={true} condensed={true} hover={true}>
                    <thead>
                      <tr>
                          <th>Messung</th>
                          <th>Abstand</th>
                          <th>Abweichung</th>
                      </tr>
                    </thead>
                    <tbody>
                      {this.renderMesswertTabelle()}
                      <tr style={{textAlign: 'center', verticalAlign: 'middle'}}>
                        <td style={{textAlign: 'center', verticalAlign: 'middle'}}>&#916;</td>
                        <td>
                          <InputGroup>
                            <FormControl
                              type="text"
                              value={this.deltaHoehe !== undefined ? this.deltaHoehe : ''}
                              readOnly={true}
                              className={this.deltaHoehe ? (((this.deltaHoehe >= this.minDeltaHoehe) && (this.deltaHoehe <= this.maxDeltaHoehe)) ? 'text-success' : 'text-danger') : ''}
                              name={`${this.props.kategorieguid}[${this.props.elementguid}][2][0]`}/>
                            <InputGroup.Addon>mm</InputGroup.Addon>
                          </InputGroup>
                        </td>
                        <td>
                          <InputGroup>
                            <FormControl
                              type="number"
                              value={this.deltaAbweichung !== undefined ? this.deltaAbweichung : ''}
                              defaultValue={this.deltaAbweichung ? this.deltaAbweichung.toFixed(2) : ''}
                              readOnly={true}
                              className={this.deltaAbweichung ? (this.deltaAbweichung > this.maxDeltaAbweichung ? 'text-danger' : 'text-success') : ''}
                              name={`${this.props.kategorieguid}[${this.props.elementguid}][2][1]`}/>
                            <InputGroup.Addon>%</InputGroup.Addon>
                          </InputGroup>
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                </Col>
                <Col xs={2}/>
              </Row>
              <Row>
                <Col xs={2}/>
                <Col xs={8}>
                  <InputGroup>
                    <InputGroup.Addon>Beurteilung der Säule:</InputGroup.Addon>
                    <FormControl
                      type="text"
                      readOnly={true}
                      value={this.statusBeurteilung()}
                      className={this.statusBeurteilung() === 'in Ordnung' ? 'text-success' : 'text-danger'}
                      name={`${this.props.kategorieguid}[${this.props.elementguid}][3][0]`}/>
                  </InputGroup>
                </Col>
                <Col xs={2}/>
              </Row>
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./~/tslint-loader!./src/components/vorlagen/ses/SepBeurteilungSaeule.tsx